<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { readAnnotationMediaLayout } from '../annotation-media-layout.js'

const props = defineProps({
  attachment: { type: Object, default: null },
  kind: { type: String, default: '' },
  mediaLayout: { type: Object, default: null }
})

const emit = defineEmits(['close'])
const viewport = ref(null)
const image = ref(null)
const video = ref(null)
const videoLayout = ref(null)
const previewViewportHeight = ref(window.innerHeight)
const scale = ref(1)
const offsetX = ref(0)
const offsetY = ref(0)
const dragging = ref(false)
const dragStart = ref({ x: 0, y: 0, offsetX: 0, offsetY: 0 })
const pointers = new Map()
let pinchDistance = 0
let pinchScale = 1

const open = computed(() => Boolean(props.attachment?.url && ['image', 'video'].includes(props.kind)))
const imageStyle = computed(() => ({
  transform: `translate3d(${offsetX.value}px,${offsetY.value}px,0) scale(${scale.value})`,
  transition: dragging.value || pointers.size > 1 ? 'none' : 'transform 160ms ease'
}))
const videoCardClass = computed(() => videoLayout.value
  ? `is-media-${videoLayout.value.orientation}`
  : 'is-media-pending')
const videoCardStyle = computed(() => {
  if (!videoLayout.value) return null
  const maxVideoHeight = Math.min(previewViewportHeight.value * 0.72, 720)
  return {
    '--annotation-preview-video-ratio': String(videoLayout.value.ratio),
    '--annotation-preview-video-card-width': `${Math.min(860, maxVideoHeight * videoLayout.value.ratio + 28)}px`
  }
})

function resetTransform() {
  scale.value = 1
  offsetX.value = 0
  offsetY.value = 0
  dragging.value = false
  pointers.clear()
  pinchDistance = 0
  pinchScale = 1
}

function renderedMetrics() {
  if (!viewport.value || !image.value?.naturalWidth || !image.value?.naturalHeight) return null
  const viewportWidth = viewport.value.clientWidth
  const viewportHeight = viewport.value.clientHeight
  const ratio = Math.min(viewportWidth / image.value.naturalWidth, viewportHeight / image.value.naturalHeight)
  return {
    viewportWidth,
    viewportHeight,
    renderedWidth: image.value.naturalWidth * ratio,
    renderedHeight: image.value.naturalHeight * ratio
  }
}

function clampOffsets() {
  const metrics = renderedMetrics()
  if (!metrics) return
  const maxX = Math.max(0, (metrics.renderedWidth * scale.value - metrics.viewportWidth) / 2)
  const maxY = Math.max(0, (metrics.renderedHeight * scale.value - metrics.viewportHeight) / 2)
  offsetX.value = Math.min(maxX, Math.max(-maxX, offsetX.value))
  offsetY.value = Math.min(maxY, Math.max(-maxY, offsetY.value))
}

function updateScale(value) {
  scale.value = Math.min(4, Math.max(1, value))
  if (scale.value === 1) {
    offsetX.value = 0
    offsetY.value = 0
  } else {
    clampOffsets()
  }
}

function pointerDown(event) {
  if (props.kind !== 'image') return
  event.currentTarget?.setPointerCapture?.(event.pointerId)
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (pointers.size === 1) {
    dragging.value = scale.value > 1
    dragStart.value = { x: event.clientX, y: event.clientY, offsetX: offsetX.value, offsetY: offsetY.value }
  } else if (pointers.size === 2) {
    const [first, second] = [...pointers.values()]
    pinchDistance = Math.hypot(first.x - second.x, first.y - second.y)
    pinchScale = scale.value
    dragging.value = false
  }
}

function pointerMove(event) {
  if (!pointers.has(event.pointerId)) return
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (pointers.size === 2) {
    const [first, second] = [...pointers.values()]
    const distance = Math.hypot(first.x - second.x, first.y - second.y)
    if (pinchDistance) updateScale(distance / pinchDistance * pinchScale)
    return
  }
  if (!dragging.value) return
  offsetX.value = dragStart.value.offsetX + event.clientX - dragStart.value.x
  offsetY.value = dragStart.value.offsetY + event.clientY - dragStart.value.y
  clampOffsets()
}

function pointerUp(event) {
  pointers.delete(event.pointerId)
  if (pointers.size < 2) {
    pinchDistance = 0
    pinchScale = scale.value
  }
  if (!pointers.size) {
    dragging.value = false
    clampOffsets()
  }
}

function close() {
  if (video.value) {
    video.value.pause?.()
    video.value.currentTime = 0
  }
  emit('close')
}

function toggleZoom() {
  if (props.kind === 'image') updateScale(scale.value > 1 ? 1 : 2)
}

function captureVideoLayout(event) {
  videoLayout.value = readAnnotationMediaLayout(event?.currentTarget)
}

function download() {
  const source = props.attachment?.url
  if (!source) return
  const link = document.createElement('a')
  link.href = source
  link.download = props.attachment?.name || 'attachment'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

function handleKeydown(event) {
  if (event.key === 'Escape' && open.value) close()
}

function handleViewportResize() {
  previewViewportHeight.value = window.innerHeight
  clampOffsets()
}

watch(() => [open.value, props.attachment?.url, props.kind, props.mediaLayout?.ratio], async ([value]) => {
  videoLayout.value = props.mediaLayout || null
  if (!value) {
    resetTransform()
    return
  }
  await nextTick()
  video.value?.play?.().catch(() => {})
})

window.addEventListener('keydown', handleKeydown)
window.addEventListener('resize', handleViewportResize)
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', handleViewportResize)
})
</script>

<template>
  <div
    v-if="open"
    class="annotation-media-preview"
    role="dialog"
    aria-modal="true"
    :aria-label="attachment?.name || '附件预览'"
    @pointerdown.stop
  >
    <div class="annotation-media-preview__actions">
      <button v-if="kind === 'image'" type="button" aria-label="下载图片" @click="download">↓</button>
      <button type="button" aria-label="关闭预览" @click="close">×</button>
    </div>
    <div
      ref="viewport"
      class="annotation-media-preview__viewport"
      @pointerdown="pointerDown"
      @pointermove="pointerMove"
      @pointerup="pointerUp"
      @pointercancel="pointerUp"
      @wheel.prevent="kind === 'image' && updateScale(scale + ($event.deltaY < 0 ? .2 : -.2))"
      @dblclick.prevent="toggleZoom"
      @click.self="close"
    >
      <img
        v-if="kind === 'image'"
        ref="image"
        :src="attachment.url"
        :alt="attachment.name || '标注图片'"
        :style="imageStyle"
        draggable="false"
        @load="resetTransform"
      />
      <section
        v-else
        class="annotation-media-preview__video-card"
        :class="videoCardClass"
        :style="videoCardStyle"
      >
        <video
          ref="video"
          :src="attachment.url"
          controls
          playsinline
          autoplay
          loop
          @loadedmetadata="captureVideoLayout"
        ></video>
      </section>
    </div>
  </div>
</template>
